import { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { 
  Search, 
  Clock, 
  Package, 
  Truck, 
  CheckCircle, 
  XCircle, 
  RotateCcw,
  Trash2,
  Save,
  X,
  DollarSign,
  Edit2,
  User,
  Calendar,
  Filter,
  ChevronDown,
  ChevronUp
} from "lucide-react";

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800",
  ordered: "bg-blue-100 text-blue-800",
  shipped: "bg-purple-100 text-purple-800",
  delivered: "bg-green-100 text-green-800",
  canceled: "bg-gray-100 text-gray-800",
  return: "bg-red-100 text-red-800"
};

const statusIcons = {
  pending: Clock,
  ordered: Package,
  shipped: Truck,
  delivered: CheckCircle,
  canceled: XCircle,
  return: RotateCcw
};

export default function OrdersList({ orders = [], onRefresh, user }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Advanced Filter State
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    techName: "",
    partSource: "",
    status: "",
    dateFrom: "",
    dateTo: ""
  });

  const canEdit = user?.role === 'admin' || user?.role === 'part_manager';

  // Get unique tech names and part sources for filter dropdowns
  const uniqueTechs = useMemo(() => {
    const techs = [...new Set(orders.map(o => o.assigned_tech_name).filter(Boolean))];
    return techs.sort();
  }, [orders]);

  const uniqueSources = useMemo(() => {
    const sources = [...new Set(orders.map(o => o.part_source).filter(Boolean))];
    return sources.sort();
  }, [orders]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter(v => v !== "").length;
  }, [filters]);

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      techName: "",
      partSource: "",
      status: "",
      dateFrom: "",
      dateTo: ""
    });
    setSearchQuery("");
  };

  // Filter and sort orders - newest first
  const filteredOrders = useMemo(() => {
    return orders
      .filter((order) => {
        if (!order) return false;
        
        // Search query filter
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          const matchesSearch = 
            order.job_number?.toLowerCase().includes(query) ||
            order.part_number?.toLowerCase().includes(query) ||
            order.assigned_tech_name?.toLowerCase().includes(query) ||
            order.notes?.toLowerCase().includes(query);
          if (!matchesSearch) return false;
        }
        
        // Tech name filter
        if (filters.techName && order.assigned_tech_name !== filters.techName) {
          return false;
        }
        
        // Part source filter
        if (filters.partSource && order.part_source !== filters.partSource) {
          return false;
        }
        
        // Status filter
        if (filters.status && order.status !== filters.status) {
          return false;
        }
        
        // Date from filter
        if (filters.dateFrom) {
          const orderDate = new Date(order.created_at);
          const fromDate = new Date(filters.dateFrom);
          if (orderDate < fromDate) return false;
        }
        
        // Date to filter
        if (filters.dateTo) {
          const orderDate = new Date(order.created_at);
          const toDate = new Date(filters.dateTo);
          toDate.setHours(23, 59, 59, 999); // End of day
          if (orderDate > toDate) return false;
        }
        
        return true;
      })
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [orders, searchQuery, filters]);

  const startEdit = (order) => {
    setEditingId(order.id);
    setEditData({
      part_number: order.part_number || "",
      part_source: order.part_source || "Amazon",
      quantity: order.quantity || 1,
      part_cost: order.part_cost || 0,
      calculated_price: order.calculated_price || 0,
      status: order.status || "pending",
      part_eta: order.part_eta || "",
      tracking_number: order.tracking_number || "",
      notes: order.notes || ""
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const calculateMarkup = (cost, source) => {
    const partCost = parseFloat(cost) || 0;
    
    if (source === "Marcone") {
      // Marcone: Cost + $12 + 8% tax, NO markup
      return (partCost + 12) * 1.08;
    }
    if (source === "Tech Purchase") {
      // Tech Purchase: as-is
      return Math.max(partCost, 25);
    }
    
    // Amazon, Stock, Truck Part, Others: Markup first, then 8% tax on original
    // Step 1: Markup tiers based on ORIGINAL cost
    let markupMultiplier;
    if (partCost < 25) markupMultiplier = 2.5;        // 150%
    else if (partCost <= 50) markupMultiplier = 2;    // 100%
    else if (partCost <= 100) markupMultiplier = 1.5; // 50%
    else if (partCost <= 200) markupMultiplier = 1.35; // 35%
    else markupMultiplier = 1.25;                      // 25%
    
    // Step 2: Apply markup on original cost
    const afterMarkup = partCost * markupMultiplier;
    
    // Step 3: Add 8% tax on ORIGINAL cost only
    const taxAmount = partCost * 0.08;
    
    // Step 4: Final = marked up price + tax
    return Math.max(afterMarkup + taxAmount, 25);
  };

  const updateField = (field, value) => {
    const newData = { ...editData, [field]: value };
    if (field === 'part_cost' || field === 'part_source') {
      newData.calculated_price = calculateMarkup(
        field === 'part_cost' ? value : newData.part_cost,
        field === 'part_source' ? value : newData.part_source
      );
    }
    setEditData(newData);
  };

  const saveChanges = async (orderId) => {
    setSaving(true);
    try {
      await axios.patch(`/orders/${orderId}`, {
        part_number: editData.part_number,
        part_source: editData.part_source,
        quantity: parseInt(editData.quantity) || 1,
        part_cost: parseFloat(editData.part_cost) || 0,
        calculated_price: parseFloat(editData.calculated_price) || 0,
        status: editData.status,
        part_eta: editData.part_eta || null,
        tracking_number: editData.tracking_number || null,
        notes: editData.notes || null
      });
      toast.success("Saved!");
      setEditingId(null);
      if (onRefresh) await onRefresh();
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm("Delete this order?")) return;
    try {
      await axios.delete(`/orders/${orderId}`);
      toast.success("Deleted!");
      if (onRefresh) await onRefresh();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const quickStatusChange = async (orderId, newStatus) => {
    try {
      await axios.patch(`/orders/${orderId}`, { status: newStatus });
      toast.success(`${newStatus}`);
      if (onRefresh) await onRefresh();
    } catch (error) {
      toast.error("Failed");
    }
  };

  return (
    <div>
      {/* Search and Filter Bar */}
      <div className="mb-3 space-y-2">
        {/* Search + Filter Toggle */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search job #, part #, tech, notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9"
            />
          </div>
          <Button
            variant={showFilters ? "default" : "outline"}
            size="sm"
            className="h-9 px-3"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="w-4 h-4" />
            {activeFilterCount > 0 && (
              <span className="ml-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
            {showFilters ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
          </Button>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && (
          <div className="bg-slate-50 rounded-lg p-3 space-y-3 border">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-slate-700">Filters</span>
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 text-xs text-red-600">
                  Clear All
                </Button>
              )}
            </div>
            
            {/* Filter Grid */}
            <div className="grid grid-cols-2 gap-2">
              {/* Tech Name Filter */}
              <div>
                <label className="text-xs text-slate-500 block mb-1">Tech</label>
                <select
                  value={filters.techName}
                  onChange={(e) => setFilters({...filters, techName: e.target.value})}
                  className="w-full h-9 px-2 border rounded-md text-sm bg-white"
                >
                  <option value="">All Techs</option>
                  {uniqueTechs.map(tech => (
                    <option key={tech} value={tech}>{tech}</option>
                  ))}
                </select>
              </div>

              {/* Part Source Filter */}
              <div>
                <label className="text-xs text-slate-500 block mb-1">Source</label>
                <select
                  value={filters.partSource}
                  onChange={(e) => setFilters({...filters, partSource: e.target.value})}
                  className="w-full h-9 px-2 border rounded-md text-sm bg-white"
                >
                  <option value="">All Sources</option>
                  {uniqueSources.map(source => (
                    <option key={source} value={source}>{source}</option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="text-xs text-slate-500 block mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                  className="w-full h-9 px-2 border rounded-md text-sm bg-white"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="ordered">Ordered</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="return">Return</option>
                  <option value="canceled">Canceled</option>
                </select>
              </div>

              {/* Empty cell for alignment */}
              <div></div>

              {/* Date From */}
              <div>
                <label className="text-xs text-slate-500 block mb-1">From Date</label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                  className="h-9"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="text-xs text-slate-500 block mb-1">To Date</label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                  className="h-9"
                />
              </div>
            </div>

            {/* Results count */}
            <div className="text-xs text-slate-500 pt-1">
              Showing {filteredOrders.length} of {orders.length} orders
            </div>
          </div>
        )}
      </div>

      {/* Orders Grid - 2 cols mobile, 4 cols desktop */}
      {filteredOrders.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <p>No orders found</p>
          {activeFilterCount > 0 && (
            <Button variant="link" onClick={clearFilters} className="mt-2">
              Clear filters
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {filteredOrders.map((order) => {
            const StatusIcon = statusIcons[order.status] || Clock;
            
            return (
              <Card key={order.id} className="overflow-hidden">
                <CardContent className="p-2">
                  {/* Compact Card View */}
                  <div className="space-y-1">
                    {/* Header: Job # + Status */}
                    <div className="flex justify-between items-start">
                      <p className="font-bold text-sm select-all cursor-text" title="Click to select">#{order.job_number}</p>
                      <Badge className={`${statusColors[order.status]} text-[10px] px-1 py-0`}>
                        {order.status}
                      </Badge>
                    </div>
                    
                    {/* Part Info - Selectable */}
                    <p className="text-xs text-slate-600 select-all cursor-text break-all" title="Click to select">
                      {order.part_number || 'N/A'} {order.quantity > 1 && <span className="font-semibold text-violet-600">x{order.quantity}</span>}
                    </p>
                    <p className="text-[10px] text-slate-500 select-all cursor-text">
                      {order.part_source}
                      {order.brand && <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">🏷️ {order.brand}</span>}
                    </p>
                    
                    {/* Tech Name */}
                    <div className="flex items-center gap-1 text-[10px] text-slate-500">
                      <User className="w-3 h-3" />
                      <span className="select-all cursor-text">{order.assigned_tech_name || 'Unassigned'}</span>
                    </div>
                    
                    {/* Date */}
                    <div className="flex items-center gap-1 text-[10px] text-slate-500">
                      <Calendar className="w-3 h-3" />
                      <span>{order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}</span>
                    </div>
                    
                    {/* ETA - Show if available and status is ordered/shipped */}
                    {order.part_eta && (order.status === 'ordered' || order.status === 'shipped') && (
                      <div className="flex items-center gap-1 text-[10px] text-blue-600 font-medium">
                        <Truck className="w-3 h-3" />
                        <span>ETA: {new Date(order.part_eta).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      </div>
                    )}
                    
                    {/* Prices: Cost & Total */}
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500">Cost: ${(order.part_cost || 0).toFixed(2)}</span>
                      <span className="font-bold text-green-600">${(order.calculated_price || 0).toFixed(2)}</span>
                    </div>
                    
                    {/* Actions */}
                    {canEdit && (
                      <div className="flex gap-1 pt-1">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1 h-7 text-xs px-2"
                          onClick={() => startEdit(order)}
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        {order.status === 'pending' && (
                          <Button 
                            size="sm" 
                            className="flex-1 h-7 text-xs px-2 bg-blue-600"
                            onClick={() => quickStatusChange(order.id, 'ordered')}
                          >
                            Order
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit Modal - Centered */}
      {editingId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50" 
            onClick={cancelEdit}
          />
          
          {/* Edit Panel - Centered Modal */}
          <div className="relative bg-white w-full max-w-md rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white flex justify-between items-center p-4 border-b z-10">
              <h3 className="font-bold text-lg">Edit Order</h3>
              <Button variant="ghost" size="sm" onClick={cancelEdit} className="h-8 w-8 p-0">
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Edit Form */}
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-500">Part #</label>
                  <Input
                    value={editData.part_number}
                    onChange={(e) => updateField('part_number', e.target.value)}
                    className="h-9"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Source</label>
                  <select
                    value={editData.part_source}
                    onChange={(e) => updateField('part_source', e.target.value)}
                    className="w-full h-9 px-2 border rounded-md text-sm"
                  >
                    <option value="Amazon">Amazon</option>
                    <option value="Marcone">Marcone</option>
                    <option value="Stock">Stock</option>
                    <option value="Tech Purchase">Tech</option>
                    <option value="Truck Part">Truck</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs text-slate-500">Qty</label>
                  <Input
                    type="number"
                    min="1"
                    value={editData.quantity || 1}
                    onChange={(e) => updateField('quantity', parseInt(e.target.value) || 1)}
                    className="h-9"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Cost $</label>
                  <Input
                    type="number"
                    value={editData.part_cost}
                    onChange={(e) => updateField('part_cost', e.target.value)}
                    className="h-9"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Total $</label>
                  <Input
                    type="number"
                    value={typeof editData.calculated_price === 'number' ? editData.calculated_price.toFixed(2) : editData.calculated_price}
                    onChange={(e) => updateField('calculated_price', e.target.value)}
                    className="h-9 bg-green-50"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-500">Status</label>
                  <select
                    value={editData.status}
                    onChange={(e) => updateField('status', e.target.value)}
                    className="w-full h-9 px-2 border rounded-md text-sm"
                  >
                    <option value="pending">Pending</option>
                    <option value="ordered">Ordered</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="return">Return</option>
                    <option value="canceled">Canceled</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500">ETA</label>
                  <Input
                    type="date"
                    value={editData.part_eta}
                    onChange={(e) => updateField('part_eta', e.target.value)}
                    className="h-9"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-xs text-slate-500">Tracking #</label>
                <Input
                  value={editData.tracking_number}
                  onChange={(e) => updateField('tracking_number', e.target.value)}
                  placeholder="Tracking number"
                  className="h-9"
                />
              </div>
              
              <div>
                <label className="text-xs text-slate-500">Notes</label>
                <textarea
                  value={editData.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  placeholder="Notes..."
                  className="w-full p-2 border rounded-md text-sm min-h-[60px]"
                />
              </div>
            </div>
            
            {/* Action Buttons - Sticky Footer */}
            <div className="sticky bottom-0 bg-white p-4 border-t flex gap-2">
              <Button 
                className="flex-1 h-10 bg-blue-600 hover:bg-blue-700" 
                onClick={() => saveChanges(editingId)} 
                disabled={saving}
              >
                <Save className="w-4 h-4 mr-1" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button 
                variant="destructive" 
                className="h-10 px-4"
                onClick={() => { deleteOrder(editingId); cancelEdit(); }}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
